// Per-persona chat hook. Owns the in-flight request, suggestion state, and
// abort handling. Outer App owns the persisted history map.

import { useCallback, useRef, useState } from 'react';
import type { ChatMessage, ChatState, LibraryWord, Persona, Story, Suggestion } from '@/lib/types';
import { getAIClient } from '@/lib/ai/client';
import { Events, track } from '@/lib/observability';

interface UseChatArgs {
  persona: Persona;
  state: ChatState;
  library: LibraryWord[];
  /** Durable user facts surfaced in the system prompt. */
  memoryFacts?: string[];
  appendMessage: (msg: ChatMessage) => void;
  clearStoryContext: () => void;
  /** Fired after every successful AI reply; used to drive memory extraction. */
  onTurnComplete?: () => void;
}

interface UseChat {
  loading: boolean;
  error: string | null;
  suggestions: Suggestion[] | null;
  suggestionsLoading: boolean;
  suggestionsError: string | null;
  send: (text: string) => Promise<void>;
  retry: () => Promise<void>;
  fetchSuggestions: () => Promise<void>;
  clearSuggestions: () => void;
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function useChat({
  persona,
  state,
  library,
  memoryFacts,
  appendMessage,
  clearStoryContext,
  onTurnComplete,
}: UseChatArgs): UseChat {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  const aborter = useRef<AbortController | null>(null);
  const lastTriedRef = useRef<{ history: ChatMessage[]; lastStory?: Story | null } | null>(null);

  const requestReply = useCallback(
    async (history: ChatMessage[], lastStory: Story | null | undefined) => {
      setLoading(true);
      setError(null);
      aborter.current?.abort();
      aborter.current = new AbortController();
      lastTriedRef.current = { history, lastStory };
      const replyStart = Date.now();
      try {
        const hskCap = persona.customInput?.hskLevel ?? persona.hskLevel ?? null;
        const reply = await getAIClient().chat(
          {
            persona,
            history,
            library,
            lastStory: lastStory ?? null,
            ...(memoryFacts && memoryFacts.length > 0 ? { memoryFacts } : {}),
            ...(hskCap ? { hskCap } : {}),
          },
          aborter.current.signal,
        );
        appendMessage({
          id: newId(),
          from: 'them',
          hz: reply.hz,
          py: reply.py,
          en: reply.en,
          words: reply.words,
          ts: Date.now(),
        });
        track(Events.ReplyReceived, {
          personaId: persona.id,
          latencyMs: Date.now() - replyStart,
        });
        if (lastStory) clearStoryContext();
        onTurnComplete?.();
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setError((err as Error).message ?? 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    },
    [persona, library, memoryFacts, appendMessage, clearStoryContext, onTurnComplete],
  );

  const send = useCallback(
    async (text: string) => {
      const userMsg: ChatMessage = {
        id: newId(),
        from: 'me',
        hz: text,
        py: '',
        en: '',
        words: [],
        ts: Date.now(),
      };
      appendMessage(userMsg);
      setSuggestions(null);
      const nextHistory = [...state.history, userMsg];
      await requestReply(nextHistory, state.lastStory);
    },
    [appendMessage, state.history, state.lastStory, requestReply],
  );

  const retry = useCallback(async () => {
    const last = lastTriedRef.current;
    if (!last) return;
    await requestReply(last.history, last.lastStory);
  }, [requestReply]);

  const fetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    try {
      const items = await getAIClient().suggest({ persona, history: state.history });
      setSuggestions(items);
    } catch (err) {
      setSuggestionsError((err as Error).message ?? 'Could not fetch suggestions.');
    } finally {
      setSuggestionsLoading(false);
    }
  }, [persona, state.history]);

  const clearSuggestions = useCallback(() => setSuggestions(null), []);

  return {
    loading,
    error,
    suggestions,
    suggestionsLoading,
    suggestionsError,
    send,
    retry,
    fetchSuggestions,
    clearSuggestions,
  };
}

/** Build a starter chat state for a persona, seeding the greeting message. */
export function seedChatState(persona: Persona): ChatState {
  return {
    history: [
      {
        id: newId(),
        from: 'them',
        hz: persona.greeting.hz,
        py: persona.greeting.py,
        en: persona.greeting.en,
        words: persona.greeting.words,
        ts: Date.now(),
      },
    ],
  };
}

